import React from "react";
import Footer from "./Footer";
import { CircleCheck, Camera, CheckCircle } from "lucide-react";
import { Hourglass } from "react-loader-spinner";
import useStickyButton from "../hooks/useStickyButton";

const CheckQuality = (props) => {
  const {
    useOCRForData,
    ocrName,
    ocrIDNumber,
    isSubmitting,
    myPRSide,
    imageBlob,
    onRetake,
    onConfirm,
    onEditName,
    onEditIDNumber,
    uiConfig = {},
  } = props;

  const [isAcknowledged, setIsAcknowledged] = React.useState(false);
  const { buttonContainerRef, sentinelRef, isSticky } = useStickyButton();

  // Safe access
  const global = uiConfig?.Global || {};
  const textColor = global?.text?.fontColor || "#6b7280"; // default gray-500
  const cardBgColor = global?.card?.backgroundColor || "#f3f4f6"; // gray-50
  const cardOpacity = global?.card?.opacity || "1"; // default opacity
  const buttonBgColor = global?.button?.backgroundColor || "#2563eb"; // blue-600
  const buttonTextColor = global?.button?.fontColor || "#ffffff"; // white
  const retakeButtonTextColor = global?.button?.fontColor || "#3b82f6"; // blue-500
  const pageBgColor = global?.pageContainer?.backgroundColor || "#ffffff"; // white

  const inputRef = React.useRef(null);

  const handleRetake = () => {
    inputRef.current?.click();
  };

  const onChange = async (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.readAsArrayBuffer(file);

    reader.onload = async () => {
      onRetake(reader.result);
    };
  };

  return (
    <div
      style={{
        background: pageBgColor,
        padding: "1rem",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <h2
        style={{
          fontSize: "1.25rem",
          fontWeight: "bold",
          marginBottom: "0.5rem",
          textAlign: "center",
          color: textColor,
        }}
      >
        Check your photo quality
      </h2>
      <div>
        <div className="flex flex-col items-center mt-4 mb-4">
          <img
            src={URL.createObjectURL(imageBlob)}
            className="h-48 md:h-48 lg:h-48 w-auto rounded-lg shadow-md shadow-black"
          />
          <p
            style={{
              fontWeight: "bold",
              textAlign: "center",
              marginTop: "0.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: textColor,
            }}
          >
            MyPR {myPRSide === "front" ? "Front" : "Back"}
          </p>
        </div>

        {useOCRForData ? (
          <div
            style={{
              borderRadius: "0.75rem",
              padding: "1rem",
              marginBottom: "1.5rem",
              backgroundColor: cardBgColor,
              opacity: cardOpacity,
            }}
          >
            <ul className="space-y-2">
              <li className="flex items-center justify-center space-x-4">
                <span
                  style={{
                    color: textColor,
                  }}
                >
                  {ocrName}
                </span>
                {/* <button className="text-blue-500 hover:text-blue-600 transition-colors" onClick={onEditName}>
                  <Pencil size={18} />
                </button> */}
              </li>
              <li className="flex items-center justify-center space-x-4">
                <span
                  style={{
                    color: textColor,
                  }}
                >
                  {ocrIDNumber}
                </span>
                {/* <button className="text-blue-500 hover:text-blue-600 transition-colors" onClick={onEditIDNumber}>
                  <Pencil size={18} />
                </button> */}
              </li>
            </ul>
          </div>
        ) : null}

        <div
          style={{
            background: cardBgColor,
            opacity: cardOpacity,
            borderRadius: "0.75rem",
            padding: "1rem",
            marginBottom: "1.5rem",
          }}
        >
          <p
            style={{
              fontSize: "0.875rem",
              marginBottom: "0.75rem",
              fontWeight: "500", // font-medium
              color: textColor, // text-gray-500
            }}
          >
            PLEASE ENSURE:
          </p>
          <div className="space-y-4 mt-4">
            <ul className="space-y-2">
              <li className="flex items-center space-x-2">
                <CheckCircle className="text-green-500" />
                <span style={{ color: textColor }}>
                  The photo is clear and sharp
                </span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="text-green-500" />
                <span style={{ color: textColor }}>
                  All corners of your MyPR are visible
                </span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="text-green-500" />
                <span style={{ color: textColor }}>
                  There is no glare or reflection
                </span>
              </li>
              {useOCRForData ? (
                <li className="flex items-center space-x-2">
                  <CheckCircle className="text-green-500" />
                  <span style={{ color: textColor }}>
                    The name and ID number are correct
                  </span>
                </li>
              ) : null}
            </ul>
          </div>
        </div>
      </div>

      <div className="flex flex-1" />

      {/* Sentinel element for Intersection Observer */}
      <div ref={sentinelRef} style={{ height: "1px", visibility: "hidden" }} />

      {/* Action buttons area */}
      {isSubmitting ? (
        <div className="flex flex-col justify-center items-center mb-6">
          <Hourglass height={"32"} width={"32"} color="#1E40AF" />
          <p style={{ color: textColor, marginTop: "2rem" }}>
            Please wait while we upload your photo...
          </p>
        </div>
      ) : (
        <div 
          ref={buttonContainerRef}
          className="px-6 mb-6"
          style={{
            position: "relative",
            bottom: "auto",
            left: "auto",
            right: "auto",
            padding: "0 1.5rem",
            background: "transparent",
            zIndex: "auto",
            transition: "all 0.3s ease-in-out",
            pointerEvents: "auto",
          }}
        >
          {useOCRForData && (
            <div className="flex items-center justify-center mb-4">
              <input
                type="checkbox"
                id="acknowledgment"
                checked={isAcknowledged}
                onChange={(e) => setIsAcknowledged(e.target.checked)}
                className="mr-4 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <label
                htmlFor="acknowledgment"
                style={{ color: textColor, fontWeight: "bold" }}
                className="text-sm"
              >
                I confirm that the extracted name and ID number are correct
              </label>
            </div>
          )}
          <button
            style={{
              background: buttonBgColor,
              color: buttonTextColor,
              width: "66.666667%",
              padding: "0.75rem 1rem",
              borderRadius: "9999px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              margin: "0 auto",
              transition: "transform 0.15s ease-in-out",
              opacity: (useOCRForData && !isAcknowledged) ? "0.5" : "1",
              pointerEvents: "auto",
            }}
            onClick={onConfirm}
            disabled={isSubmitting || (useOCRForData && !isAcknowledged)}
            onMouseDown={(e) => !e.currentTarget.disabled && (e.currentTarget.style.transform = 'scale(0.95)')}
            onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            onTouchStart={(e) => !e.currentTarget.disabled && (e.currentTarget.style.transform = 'scale(0.95)')}
            onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            Confirm
            <CircleCheck className="ml-2" />
          </button>
          <button
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              paddingTop: "0.75rem", // py-3
              paddingBottom: "0.75rem",
              fontWeight: 500, // font-medium
              fontSize: "1rem", // text-base
              marginTop: "0.5rem", // mt-2
              transition: "color 0.15s ease-in-out",
              color: retakeButtonTextColor, // text-blue-500
              pointerEvents: "auto",
            }}
            onClick={handleRetake}
            onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
            onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
            onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            Retake
            <Camera className="ml-2" />
          </button>
        </div>
      )}

      <input
        ref={inputRef}
        accept="image/*"
        type="file"
        capture="environment"
        hidden
        onChange={onChange}
      />

      <Footer
        style={{
          fontSize: "0.75rem",
          color: textColor,
          marginTop: "1rem",
        }}
      />
    </div>
  );
};

export default CheckQuality;
